/**
 * In-flight deduplication and short TTL caching for idempotent GET-style API reads.
 * Reduces duplicate parallel calls (e.g. header + route guards + dashboard on one navigation).
 */

type CacheEntry<T> = {
  expiresAt: number;
  value: T;
};

const cache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

export const API_CACHE_TTL = {
  versionMs: 5 * 60 * 1000,
  profileMs: 60 * 1000,
  statsMs: 30 * 1000,
  carbonSavingsMs: 60 * 1000,
  carbonDashboardMs: 30 * 1000,
  reportingWorkspaceBundleMs: 2 * 60 * 1000,
  /** Compliance hub overview / india / export GETs (heavy readiness rebuilds). */
  complianceHubMs: 2 * 60 * 1000,
  /** Document list reads (dedupes polling + parallel page loads). */
  documentsListMs: 10 * 1000,
  documentsDetailMs: 15 * 1000,
  documentsStatsMs: 30 * 1000,
  /** Display-only billing status. Payment actions bypass this with fresh: true. */
  billingStatusMs: 20 * 1000,
  /** Session identity. Logout clears the whole cache, so this only covers short bursts. */
  sessionMs: 15 * 1000,
  /** Cited DISCOM tariff list. Profile writes clear every GET:/msme/ key. */
  discomListMs: 5 * 60 * 1000,
} as const;

export function buildApiCacheKey(method: string, path: string, queryKey = ''): string {
  return `${method}:${path}${queryKey ? `?${queryKey}` : ''}`;
}

export function clearApiRequestCache(prefix?: string): void {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

export async function cachedApiGet<T>(
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>
): Promise<T> {
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && hit.expiresAt > now) {
    return hit.value as T;
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending as Promise<T>;
  }

  const promise = fetcher()
    .then((value) => {
      cache.set(key, { expiresAt: Date.now() + ttlMs, value });
      return value;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise as Promise<T>;
}
